import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { currentUserIsAdmin } from '../../../redux-store/thunks/subscriptions.thunks';

import CollapsePanel from '../../../components/common/CollapsePanel/CollapsePanel';

import type { RootState } from 'src/types/state.types';
import type { PipeDebugInfoLog } from 'src/types/pipes.types';
import type { RouteComponentProps } from 'react-router';

import { TestID } from 'src/testID';

import './PipeDebug.css';

export const PipeDebug: React.FC<RouteComponentProps<{}, any>> = ({ params, ...props }) => {
  const [log, setLog] = useState<PipeDebugInfoLog[]>([]);
  const pipe = useSelector((state: RootState) => state.pipes[params.pipeID]);
  const pipeRunnerStacktrace =
    (pipe?.runtime?.debug_info?.stacktrace || pipe?.debug_info?.stacktrace) ?? '';
  const debugInfoList =
    (pipe?.runtime?.debug_info?.debug_info_list || pipe?.debug_info?.debug_info_list) ?? [];
  const isAdminRole = useSelector((state: RootState) => currentUserIsAdmin(state));

  useEffect(() => {
    if (isAdminRole === false) {
      props.router.push(
        `/subscription/${params.subId}/pipes/pipe/${encodeURIComponent(params.pipeID)}/dashboard`
      );
    }
  }, [params.pipeID, params.subId, isAdminRole]);

  useEffect(() => {
    if (pipeRunnerStacktrace !== '') {
      const match = pipeRunnerStacktrace.match(/ThreadID:\s*\d+/);
      const title = match ? match[0] : 'Thread';
      const isExist = log.some((l) => l.header === title);

      if (!isExist) {
        setLog((prevDebugInfoList) => [
          {
            header: title,
            body: pipeRunnerStacktrace,
          },
          ...prevDebugInfoList,
        ]);
      }
    }

    if (debugInfoList.length > 0) {
      setLog((prevDebugInfoList) => [...debugInfoList, ...prevDebugInfoList]);
    }
  }, [debugInfoList, pipeRunnerStacktrace]);

  if (log.length === 0) {
    return (
      <div
        className="no-debug-info-message-wrapper"
        data-testid={TestID.DebugInfoNoInfoToDisplayMessage}
      >
        No debug info to display
      </div>
    );
  }

  return (
    <div title="This page contains various low-level runtime information about the pipe. (This is only displayed to sesam employees, and is used for debugging problems.)">
      {log.map((debugInfo, index) => (
        <CollapsePanel
          key={index}
          title={debugInfo.header}
          startCollapsed={index === 0 ? false : true}
          testId={TestID.DebugInfoStacktraceCollapsePanel}
        >
          <pre>{debugInfo.body}</pre>
        </CollapsePanel>
      ))}
    </div>
  );
};
